import React, { useCallback, useEffect, useState, useMemo } from "react";
import { addMonths, format, getMonth, getYear, parse, parseISO, isValid } from "date-fns";
import { de, enUS } from "date-fns/locale";
import axios from "axios";
import { API_BASE_URL } from "../../../config";
import { useTranslation } from "react-i18next";
import "../../../i18n";

import useToast from "../../../components/useToast";
import DownloadBuilder from "../../../components/DownloadBuilder";
import AppButton from "../../../design-system/components/AppButton";
import AppCallout from "../../../design-system/components/AppCallout";
import { AppStatusChip } from "../../../design-system/components/AppChip";
import AppConfirmDialog from "../../../design-system/components/AppConfirmDialog";
import AppInput from "../../../design-system/components/AppInput";
import AppPeriodNavigator from "../../../design-system/components/AppPeriodNavigator";
import AppSelect from "../../../design-system/components/AppSelect";
import AppTablePane from "../../../design-system/components/AppTablePane";
import AppTable from "../../../design-system/components/AppTable";
import AppText from "../../../design-system/components/AppText";
import AppToolbar from "../../../design-system/components/AppToolbar";
import AppTooltip from "../../../design-system/components/AppTooltip";
import AppWorkspace from "../../../design-system/components/AppWorkspace";
import {
  IconChevronLeft,
  IconChevronRight,
  IconDelete,
  IconEdit,
  IconBlock,
  IconHoliday,
  IconImport,
  IconTime,
} from "../../../design-system/icons";
import "../../../design-system/styles/tokens.css";
import "../../../design-system/styles/components.css";
import AvailabilityDateField from "../../availability/AvailabilityDateField";
import PunchEntryDialog from "../../../components/punch_entry/PunchEntryDialog";
import "../reporting.css";

const ALL_EMPLOYEES_VALUE = "__all_employees__";
const TOAST_OPTIONS = {};


const DT_PARSE_PATTERNS = [
  "dd.MM.yyyy HH:mm",
  "dd.MM.yyyy HH:mm:ss",
  "yyyy-MM-dd HH:mm",
  "yyyy-MM-dd HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm",
  "yyyy-MM-dd'T'HH:mm:ss",
];

function parseBackendDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  if (typeof value === "number") {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }

  if (typeof value === "string") {
    // try ISO first
    const iso = parseISO(value);
    if (isValid(iso)) return iso;

    // try known patterns
    for (const p of DT_PARSE_PATTERNS) {
      const d = parse(value, p, new Date());
      if (isValid(d)) return d;
    }
  }

  return null;
}


function DateDisplayCell({ value, locale, showWeekday = false }) {
  const dt = parseBackendDateTime(value);
  if (!dt) return "-";

  return (
    <AppText variant="body-1" truncate>
      {format(dt, showWeekday ? "EEE, dd.MM.yyyy" : "dd.MM.yyyy", { locale })}
    </AppText>
  );
}

function CombinedPunchPeriodCell({ row, locale, stillClockedInLabel }) {
  const checkIn = parseBackendDateTime(row.check_in);
  const checkOut = parseBackendDateTime(row.check_out);

  if (row.working_type_key === "holiday") {
    return <DateDisplayCell value={row.check_in || row.date} locale={locale} showWeekday />;
  }

  if (row.working_type_key === "absence" && !checkIn) {
    return <DateDisplayCell value={row.date} locale={locale} showWeekday />;
  }

  if (!checkIn) return "-";

  const checkInLabel = format(checkIn, "EEE, dd.MM.yyyy HH:mm", { locale });

  if (row.is_open) {
    return (
      <span className="reporting-punch-period">
        <AppText as="span" variant="body-1" truncate>{checkInLabel}</AppText>
        <span className="reporting-open-status">
          <span className="reporting-open-status__dot" aria-hidden="true" />
          <AppText as="span" variant="body-1" tone="secondary">
            {stillClockedInLabel}
          </AppText>
        </span>
      </span>
    );
  }

  if (!checkOut) {
    return <AppText variant="body-1" truncate>{checkInLabel}</AppText>;
  }

  const sameDate = format(checkIn, "yyyy-MM-dd") === format(checkOut, "yyyy-MM-dd");
  const label = sameDate
    ? `${format(checkIn, "EEE, dd.MM.yyyy", { locale })} · ${format(checkIn, "HH:mm")}–${format(checkOut, "HH:mm")}`
    : `${checkInLabel} – ${format(checkOut, "EEE, dd.MM.yyyy HH:mm", { locale })}`;

  // Gleiche Schrift wie die Datumszellen (Ferien, Absenzen): body-1 wie AppTable.
  return <AppText variant="body-1" truncate>{label}</AppText>;
}


function areCellValuesEqual(field, a, b) {
  // Treat null/undefined/"" equivalently if you want; adjust as needed
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  if (aEmpty && bEmpty) return true;

  // Datetime fields: compare actual timestamp if parseable
  if (field === "check_in" || field === "check_out") {
    const da = parseBackendDateTime(a);
    const db = parseBackendDateTime(b);
    if (da && db) {
      // compare to the minute to be tolerant if formats differ by seconds
      return Math.floor(da.getTime() / 60000) === Math.floor(db.getTime() / 60000);
    }
  }

  // Numeric-ish fields (break time): compare numbers if possible
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== "" && String(b).trim() !== "") {
    return na === nb;
  }

  // Fallback: string compare
  return String(a ?? "") === String(b ?? "");
}



const Reporting = () => {
  const { t, i18n } = useTranslation();

  const [punchClock, setPunchClock] = useState([]);
  const [totalHours, setTotalHours] = useState("0:00");
  const [accessLevel, setAccessLevel] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const timeframe = "day";
  const [user_list, setUserList] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  // Stempelung, die die Planung gerade korrigiert (Datum, Beginn, Ende, Löschen).
  const [editedEntry, setEditedEntry] = useState(null);
  const [open, setOpen] = useState(false);
  const closeModal = () => setOpen(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportPreviewLoading, setIsExportPreviewLoading] = useState(false);
  const [exportPreview, setExportPreview] = useState(null);
  const [exportTimeframe, setExportTimeframe] = useState({});
  const [exportTabMode, setExportTabMode] = useState("none");
  const [exportFilters, setExportFilters] = useState({});
  const [exportResetKey, setExportResetKey] = useState(0);
  const [manualBreakCheck, setManualBreakCheck] =useState('');
  const [isLoading, setIsLoading] = useState(true);
  const tablePaneRef = React.useRef(null);
  const [tablePaneWidth, setTablePaneWidth] = useState(null);
  const token = localStorage.getItem("session_token");
  const { success, error } = useToast(TOAST_OPTIONS);


  const dfLocale = useMemo(() => {
    const lang = (i18n.language || "en").toLowerCase();
    return lang.startsWith("de") ? de : enUS; // handles "de", "de-CH", etc.
  }, [i18n.language]);



  // Initialize the report period with the current month.
  useEffect(() => {
    const now = new Date();
    setSelectedMonth(getMonth(now));
    setSelectedYear(getYear(now));
  }, []);

  const selectedPeriodDate = useMemo(() => {
    const now = new Date();
    return new Date(
      selectedYear ?? getYear(now),
      selectedMonth ?? getMonth(now),
      1
    );
  }, [selectedMonth, selectedYear]);

  /*
    Die Beschriftung kommt bewusst von hier statt aus formatPeriodTitle des DS:
    diese Seite ist zweisprachig, formatPeriodTitle formatiert fest auf de-CH.
    AppPeriodNavigator sieht periodLabel dafür vor.
  */
  const periodLabel = useMemo(
    () => format(selectedPeriodDate, "LLLL yyyy", { locale: dfLocale }),
    [dfLocale, selectedPeriodDate]
  );

  const selectReportPeriod = (date) => {
    setSelectedMonth(getMonth(date));
    setSelectedYear(getYear(date));
  };

  const stepReportPeriod = (offset) => selectReportPeriod(addMonths(selectedPeriodDate, offset));
  



// Initial Fetch From Backend
  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/reporting`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          month: selectedMonth +1,
          year: selectedYear,
          selectedUser: selectedUser,
          timeframe: timeframe,
        }
      });

      setPunchClock(response.data.punch_clock);
      setTotalHours(response.data.total_hours || "0:00");
      setSelectedEntryIds([]);
      setAccessLevel(response.data.access_level);
      setUserList(response.data.user_list);
      if (response.data.manual_break_check === "1" || response.data.manual_break_check === "X") {
        setManualBreakCheck("manual_break_hours");
      } else {
        setManualBreakCheck("break_time");
      }  
      setIsLoading(false);
    } catch (error) {
      // Handle error
      setIsLoading(false);
    }
  }, [selectedMonth, selectedUser, selectedYear, timeframe, token]);

  const currentExportMonth = () => {
    const now = new Date();
    const year = selectedYear ?? now.getFullYear();
    const month = (selectedMonth ?? now.getMonth()) + 1;
    return `${year}-${String(month).padStart(2, "0")}`;
  };

  const buildExportPreviewParams = (
    nextTimeframe,
    tabMode = exportTabMode,
    filters = exportFilters
  ) => {
    const filterParams = { filters: JSON.stringify(filters || {}) };
    if (nextTimeframe?.mode === "range") {
      if (
        !nextTimeframe.start_date ||
        !nextTimeframe.end_date ||
        nextTimeframe.end_date < nextTimeframe.start_date
      ) {
        return null;
      }
      return {
        ...filterParams,
        start_date: nextTimeframe.start_date,
        end_date: nextTimeframe.end_date,
        tab_mode: tabMode,
      };
    }
    return {
      ...filterParams,
      month: nextTimeframe?.month || currentExportMonth(),
      tab_mode: tabMode,
    };
  };

  const loadExportPreview = async (
    nextTimeframe,
    tabMode = exportTabMode,
    filters = exportFilters
  ) => {
    const params = buildExportPreviewParams(nextTimeframe, tabMode, filters);
    if (!params) return null;
    setIsExportPreviewLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/reporting/export/preview`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setExportPreview(response.data);
      return response.data;
    } catch (requestError) {
      console.error("Fehler beim Laden der Download-Vorschau:", requestError);
      error(t("notification.error"));
      return null;
    } finally {
      setIsExportPreviewLoading(false);
    }
  };

  const openExportBuilder = async () => {
    const nextTimeframe = {
      mode: "month",
      month: currentExportMonth(),
      start_date: "",
      end_date: "",
    };
    setExportPreview(null);
    setExportTimeframe(nextTimeframe);
    setExportTabMode("none");
    setExportFilters({});
    setOpen(true);
    const preview = await loadExportPreview(nextTimeframe, "none", {});
    if (!preview) return;
    setExportResetKey((current) => current + 1);
  };

  const handleExportTimeframeChange = (nextTimeframe) => {
    setExportTimeframe(nextTimeframe);
    loadExportPreview(nextTimeframe, exportTabMode, exportFilters).catch(() => {});
  };

  const handleExportFiltersChange = (nextFilters) => {
    setExportFilters(nextFilters);
    loadExportPreview(exportTimeframe, exportTabMode, nextFilters).catch(() => {});
  };

  const handleReportDownload = async ({
    format: exportFormat = "excel",
    columns: exportColumns,
    tab_mode: tabMode,
    filters,
    timeframe: selectedExportTimeframe,
    settings,
  }) => {
    if (!buildExportPreviewParams(selectedExportTimeframe)) return;
    const isPdfExport = exportFormat === "pdf";
    const extension = isPdfExport ? "pdf" : "xlsx";
    setIsExporting(true);
    try {
      const requestData = {
        format: exportFormat,
        columns: exportColumns,
        tab_mode: tabMode,
        filters,
        timeframe: selectedExportTimeframe,
        settings,
      };
    
      const response = await axios.post(`${API_BASE_URL}/api/web/reporting/export`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });
  
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: isPdfExport
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      const period = selectedExportTimeframe?.mode === "range"
        ? `${selectedExportTimeframe.start_date}_bis_${selectedExportTimeframe.end_date}`
        : selectedExportTimeframe?.month || currentExportMonth();
      link.setAttribute("download", `Report_${period}.${extension}`);
      document.body.appendChild(link);
      link.click();
  
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      closeModal();
    } catch (requestError) {
      console.error("Fehler beim Erstellen des Downloads:", requestError);
      error(t("notification.error"));
    } finally {
      setIsExporting(false);
    }
  };
  

  // Call this function initially and whenever month/year changes
  useEffect(() => {
    if (selectedMonth !== null && selectedYear !== null) {
      fetchReport();
    }
  }, [fetchReport, selectedMonth, selectedYear]);


  const handleCellEditCommit = async ({ id, field, value, originalValue, row }) => {
    if (row?.working_type === "Ferien") {
      error(t("report.holiday_overwrite_not_possible") || "Overwrite of holidays not possible");
      return;
    }
  
    const allowed = new Set(["check_in", "check_out", manualBreakCheck]);
    if (!allowed.has(field)) return;

    if (areCellValuesEqual(field, originalValue, value)) return;

    try {
      await axios.put(
        `${API_BASE_URL}/api/web/reporting/${id}`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchReport();
      success(t("notification.successful"));
    } catch (err) {
      console.error("PUT failed:", err);
      error(t("notification.error"));
      fetchReport();
    }
  };

  
  const [openDialog, setOpenDialog] = useState(false);

  const handleDialogOpen = () => {
    if (selectedEntryIds.length === 0) return;
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleConfirmDelete = async () => {
    setOpenDialog(false);
    if (selectedEntryIds.length === 0) return;
    const entryIdsToDelete = [...selectedEntryIds];
    try {
      await Promise.all(
        entryIdsToDelete.map((id) =>
          axios.delete(`${API_BASE_URL}/api/web/reporting/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      const deletedEntryIds = new Set(entryIdsToDelete);
      setPunchClock((entries) => entries.filter((entry) => !deletedEntryIds.has(entry.id)));
      setSelectedEntryIds([]);
      success(t("notification.successful"));
    } catch (requestError) {
      error(t("notification.error"));
      fetchReport();
    }
  };

  const isAdmin = ["Super_Admin", "Admin", "company_admin"].includes(accessLevel);
  const breakField = manualBreakCheck || "break_time";
  const canEditRow = (row) => isAdmin && row.working_type_key === "punch_clock";
  const canSelectRow = useCallback(
    (row) => isAdmin && row.working_type_key === "punch_clock",
    [isAdmin]
  );
  const showEmployeeName = isAdmin && !selectedUser;
  const tableBaseMinWidth = (isAdmin ? 64 : 0) + 160 + 160 + 160;
  const separateNamesMinWidth =
    tableBaseMinWidth + (showEmployeeName ? 140 + 150 : 0) + 280;
  const mergeEmployeeName =
    showEmployeeName && tablePaneWidth !== null && tablePaneWidth < separateNamesMinWidth;

  useEffect(() => {
    if (isLoading) return undefined;

    const pane = tablePaneRef.current;
    if (!pane) return undefined;

    const measure = () => {
      const nextWidth = Math.round(pane.getBoundingClientRect().width);
      setTablePaneWidth((currentWidth) => currentWidth === nextWidth ? currentWidth : nextWidth);
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [isLoading]);

  const columnsReport = [
    isAdmin && {
      key: "selected",
      label: "",
      controlKind: "selection",
      sortable: false,
    },
    showEmployeeName && !mergeEmployeeName && {
      key: "first_name",
      label: t("report.columns.first_name"),
      minWidth: 140,
      grow: 1,
      render: (value) => (
        <AppText variant="heading-4" tone="primary" truncate>
          {value || "-"}
        </AppText>
      ),
    },
    showEmployeeName && !mergeEmployeeName && {
      key: "last_name",
      label: t("report.columns.last_name"),
      minWidth: 150,
      grow: 1,
      render: (value) => (
        <AppText variant="heading-4" tone="primary" truncate>
          {value || "-"}
        </AppText>
      ),
    },
    showEmployeeName && mergeEmployeeName && {
      key: "name",
      label: t("report.columns.name"),
      minWidth: 200,
      grow: 1.4,
      sortAccessor: (row) => `${row.first_name || ""} ${row.last_name || ""}`.trim(),
      render: (_value, row) => (
        <AppText variant="heading-4" tone="primary" truncate>
          {[row.first_name, row.last_name].filter(Boolean).join(" ") || row.name || "-"}
        </AppText>
      ),
    },
    {
      key: "working_type",
      label: t("report.columns.working_type"),
      grow: 1,
      render: (_value, row) => {
        if (row.working_type_key === "holiday") {
          return (
            <AppStatusChip
              status="info"
              size="sm"
              leadingIcon={<IconHoliday />}
              className="reporting-type-chip reporting-type-chip--holiday"
            >
              {t("report.types.holiday")}
            </AppStatusChip>
          );
        }

        if (row.working_type_key === "absence") {
          return (
            <AppStatusChip
              status="error"
              size="sm"
              leadingIcon={<IconBlock />}
              className="reporting-type-chip reporting-type-chip--absence"
            >
              {row.absence_name || t("report.types.absence")}
            </AppStatusChip>
          );
        }

        return (
          <AppStatusChip status="info" size="sm" leadingIcon={<IconTime />}>
            {t("report.types.punch_clock")}
          </AppStatusChip>
        );
      },
    },
    {
      key: "punch_period",
      label: t("report.timeframe"),
      minWidth: 280,
      grow: 1,
      sortAccessor: (row) => row.check_in,
      render: (_value, row) => (
        <CombinedPunchPeriodCell
          row={row}
          locale={dfLocale}
          stillClockedInLabel={t("report.still_clocked_in")}
        />
      ),
    },
    {
      key: "working_hours",
      label: t("report.columns.working_hour"),
      grow: 1,
    },
    {
      key: breakField,
      label: t("report.columns.break_hour"),
      grow: 1,
      editable: canEditRow,
      renderEdit: (value, row, editor) => (
        <AppInput
          autoFocus
          size="sm"
          type="number"
          min="0"
          value={value ?? ""}
          onChange={(event) => editor.onChange(event.target.value)}
        />
      ),
    },
    isAdmin && {
      key: "edit_entry",
      label: "",
      // Breite und Ausrichtung kommen aus AppTable (Aktionsspalte).
      controlKind: "action",
      sortable: false,
      render: (_value, row) => (canEditRow(row) ? (
        <AppTooltip content="Stempelung bearbeiten">
          <AppButton
            variant="neutral"
            size="sm"
            iconOnly
            aria-label="Stempelung bearbeiten"
            onClick={() => setEditedEntry({
              id: row.id,
              employeeName: row.name,
              checkIn: row.check_in,
              checkOut: row.check_out,
              // Die Pause, die der Bericht anzeigt und abzieht.
              breakMinutes: row.break_time,
            })}
          >
            <IconEdit />
          </AppButton>
        </AppTooltip>
      ) : null),
    },
  ].filter(Boolean);

  const rows = punchClock;
  const columns = columnsReport;
  const noRowsLabel = t("report.no_rows");
  const openPunchCount = useMemo(
    () => punchClock.filter((row) => row.is_open).length,
    [punchClock]
  );
  const showOpenPunchCallout = isAdmin && !selectedUser && openPunchCount > 0;

  const userOptions = useMemo(() => user_list.map((userString) => {
    const [, firstName, lastName] = userString.split(",").map((part) => part.trim());
    return { value: userString, label: `${firstName} ${lastName}`.trim() };
  }), [user_list]);
  const employeeNavigationOptions = useMemo(
    () => [
      { value: ALL_EMPLOYEES_VALUE, label: t("report.all_employees") },
      ...userOptions,
    ],
    [t, userOptions]
  );
  const selectedEmployeeValue = selectedUser || ALL_EMPLOYEES_VALUE;
  const selectedEmployeeIndex = employeeNavigationOptions.findIndex(
    (option) => option.value === selectedEmployeeValue
  );
  const selectEmployeeAt = (index) => {
    const option = employeeNavigationOptions[index];
    if (option) {
      setSelectedUser(option.value === ALL_EMPLOYEES_VALUE ? "" : option.value);
    }
  };

  return (
    <div className="reporting-page">
      <AppWorkspace title={t("report.title")} loading={isLoading}>
        <AppToolbar
          className="reporting-toolbar"
          left={(
            <>
              <div className="reporting-toolbar__period">
                {/* Dieselbe Navigation wie auf /availability, /demand und /shiftplan. */}
                <AppPeriodNavigator
                  viewMode="month"
                  referenceDate={selectedPeriodDate}
                  periodLabel={periodLabel}
                  size="md"
                  className="reporting-toolbar__period-navigator"
                  previousLabel={t("availability.previous_month")}
                  nextLabel={t("availability.next_month")}
                  onPreviousPeriod={() => stepReportPeriod(-1)}
                  onNextPeriod={() => stepReportPeriod(1)}
                  onSelectDate={selectReportPeriod}
                />
              </div>
              {isAdmin ? (
                <>
                  <AppTooltip content={t("report.download")}>
                    <AppButton
                      variant="neutral"
                      size="md"
                      iconOnly
                      aria-label={t("report.download")}
                      onClick={openExportBuilder}
                      disabled={isExporting || isExportPreviewLoading}
                    >
                      <IconImport />
                    </AppButton>
                  </AppTooltip>
                  <AppTooltip content={t("report.deletion.delete_selected")}>
                    <AppButton
                      variant={selectedEntryIds.length > 0 ? "error" : "muted"}
                      size="md"
                      iconOnly
                      aria-label={t("report.deletion.delete_selected")}
                      onClick={handleDialogOpen}
                      disabled={selectedEntryIds.length === 0}
                    >
                      <IconDelete />
                    </AppButton>
                  </AppTooltip>
                </>
              ) : null}
            </>
          )}
          right={isAdmin ? (
            <div className="reporting-toolbar__employee-navigation">
              <AppTooltip content={t("team.previous_user") || "Vorherige Person"}>
                <AppButton
                  variant="neutral"
                  size="sm"
                  iconOnly
                  aria-label={t("team.previous_user") || "Vorherige Person"}
                  onClick={() => selectEmployeeAt(selectedEmployeeIndex - 1)}
                  disabled={selectedEmployeeIndex <= 0}
                >
                  <IconChevronLeft />
                </AppButton>
              </AppTooltip>
              <div className="reporting-toolbar__user">
                <AppSelect
                  size="md"
                  value={selectedEmployeeValue}
                  onChange={(event) => setSelectedUser(
                    event.target.value === ALL_EMPLOYEES_VALUE ? "" : event.target.value
                  )}
                  options={employeeNavigationOptions}
                  disabled={employeeNavigationOptions.length <= 1}
                  searchable
                  searchPlaceholder={t("team.select_user") || "Person suchen"}
                  placeholder={t("team.select_user") || "Person auswählen"}
                  aria-label={t("team.select_user") || "Person auswählen"}
                />
              </div>
              <AppTooltip content={t("team.next_user") || "Nächste Person"}>
                <AppButton
                  variant="neutral"
                  size="sm"
                  iconOnly
                  aria-label={t("team.next_user") || "Nächste Person"}
                  onClick={() => selectEmployeeAt(selectedEmployeeIndex + 1)}
                  disabled={
                    selectedEmployeeIndex < 0 ||
                    selectedEmployeeIndex >= employeeNavigationOptions.length - 1
                  }
                >
                  <IconChevronRight />
                </AppButton>
              </AppTooltip>
            </div>
          ) : null}
        />

        {showOpenPunchCallout ? (
          <AppCallout
            variant="info"
            title={t("report.open_punch_callout_title")}
            description={t("report.open_punch_callout_description", {
              count: openPunchCount,
            })}
          />
        ) : null}

        <AppTablePane ref={tablePaneRef} className="reporting-table-pane">
          <AppTable
            className="reporting-table"
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            selectedRowIds={selectedEntryIds}
            onSelectedRowIdsChange={setSelectedEntryIds}
            isRowSelectable={canSelectRow}
            emptyState={noRowsLabel}
            footer={(
              <>
                <AppTable.FooterMeta>
                  <span>{t("report.total_working_hours")}: {totalHours}</span>
                </AppTable.FooterMeta>
                <AppTable.FooterSummary>
                  <span>
                    {selectedEntryIds.length > 0
                      ? t("report.selected_entries", { count: selectedEntryIds.length })
                      : t("report.entries", { count: rows.length })}
                  </span>
                </AppTable.FooterSummary>
              </>
            )}
            onCellEditCommit={handleCellEditCommit}
          />
        </AppTablePane>
      </AppWorkspace>

      <PunchEntryDialog
        entry={editedEntry}
        onClose={() => setEditedEntry(null)}
        onChanged={() => {
          success(t("notification.successful"));
          setSelectedEntryIds([]);
          fetchReport();
        }}
      />

      <AppConfirmDialog
        open={openDialog}
        size="sm"
        title={t("report.deletion.bulk_title")}
        description={t("report.deletion.bulk_question", { count: selectedEntryIds.length })}
        cancelLabel={t("report.deletion.cancel")}
        confirmLabel={t("report.deletion.confirm")}
        confirmTone="error"
        confirmIcon={<IconDelete />}
        onClose={handleDialogClose}
        onConfirm={handleConfirmDelete}
      />

      <DownloadBuilder
        open={open}
        onClose={closeModal}
        title="Download erstellen"
        documentTitle="Report"
        description={`${exportPreview?.period_label || "Aktueller Zeitraum"} · ${exportPreview?.scope_label || "Sichtbare Mitarbeitende"}`}
        columns={exportPreview?.columns || []}
        defaultColumnIds={exportPreview?.default_column_ids || []}
        tabModes={exportPreview?.tab_modes || []}
        defaultTabMode={exportPreview?.default_tab_mode || "none"}
        timeframeOptions={[
          { id: "month", label: "Monat" },
          { id: "range", label: "Benutzerdefiniert" },
        ]}
        defaultTimeframe={exportTimeframe}
        onTimeframeChange={handleExportTimeframeChange}
        filterGroups={exportPreview?.filter_groups || []}
        defaultFilters={exportPreview?.default_filters || {}}
        onFiltersChange={handleExportFiltersChange}
        onTabModeChange={(tabMode) => {
          setExportTabMode(tabMode);
          loadExportPreview(exportTimeframe, tabMode, exportFilters).catch(() => {});
        }}
        resetKey={exportResetKey}
        previewRows={exportPreview?.preview_rows || []}
        previewSections={exportPreview?.preview_sections || []}
        previewLoading={isExportPreviewLoading}
        isDownloading={isExporting}
        onDownload={handleReportDownload}
        dateFieldComponent={AvailabilityDateField}
        pdfEnabled
      />
    </div>
  );
};

export default Reporting;
